import React, { useState } from 'react';
import HoverMenu from 'ats/components/hover-menu/HoverMenu';
import { observer } from 'mobx-react';
import { applicantStore } from 'ats/pages/applicants/ApplicantStore';
import ColumnSelector from 'ats/components/column-selector/ColumnSelector';
import { drawerStore } from 'ats/components/drawer/DrawerStore';
import {
	candidateSeniorityOptionLabel,
	candidateHiringOptions
} from 'components/candidates/profile/scorecards/CandidateScorecardStore';
import { candidatesStore } from 'components/candidates/CandidatesStore';
import UserProfileAvatar from 'ats/components/user-profile-avatar/UserProfileAvatar';
import { eventManager } from 'ats/utils/EventManager';
import moment from 'moment';
import ActionMenu from 'ats/components/action-menu/ActionMenu';
import { copyTextToClipboard } from 'ats/utils/copyToClipboard';
import AtsTooltip from 'ats/components/ats-tooltip/AtsTooltip';
import EllipsisTooltipLabel from 'ats/components/ellipsis-tooltip-label/EllipsisTooltipLabel';
import InternalStageCell from 'ats/components/internal-stage-cell/InternalStageCell';
import BulkActionsBar from 'ats/components/bulk-actions-bar/BulkActionsBar';
import PendingTaskBadge from 'ats/components/pending-task-badge/PendingTaskBadge';
import LatestTaskCell from 'ats/components/latest-task-cell/LatestTaskCell';
import { dialogStore } from 'components/layout/modals/DialogStore';
import { notificationStore } from 'components/layout/notifications/NotificationStore';
import { modalStore } from 'components/layout/modals/ModalStore';
import { AssignToJobModal } from './components/AssignToJobModal';
import { offerJobApplicationCascade } from 'ats/utils/applicantStageCascade';
import { hasPermission, PERMISSIONS } from 'shared/security/rolePermissions';
import ApplicantStageIndicator from 'ats/components/applicant-stage-indicator/ApplicantStageIndicator';
import AtsTableSkeleton from 'ats/components/ats-table-skeleton/AtsTableSkeleton';

function ApplicantsTable({
	data,
	onClickRow,
	activeRowId,
	sort,
	onSort,
	onSuccess,
	visibleColumns,
	onVisibleColumnsChange,
	isEmpty,
	emptyState,
	loading
}) {
	const [selectedIds, setSelectedIds] = useState([]);
	const [menuOpenRowId, setMenuOpenRowId] = useState(null);

	const getHireabilityTooltipLabel = hireability => {
		if (!hireability) {
			return 'Not set';
		}
		return (
			candidateHiringOptions.find(option => option.value === hireability)
				?.text || hireability
		);
	};

	const getApplicantStage = value =>
		applicantStore.stageOptions.find(stage => stage.value === value);

	const getApplicantStatus = value =>
		applicantStore.statusOptions.find(status => status.value === value);

	const getEnglishLevel = value =>
		candidatesStore.englishLevelOptions.find(level => level.value === value);

	const isColumnSorted = column => {
		if (!sort || sort.column !== column) return null;
		const icon = sort.asc ? 'arrow-up-icon' : 'arrow-down-icon';
		return (
			<span className="sort-icon">
				<img src={`/ats/icons/${icon}.svg`} alt="" />
			</span>
		);
	};

	const handleHeaderClick = column => {
		onSort?.(column);
	};

	const resolvedVisibleColumns =
		visibleColumns || applicantStore.defaultTableColumnsOptions;

	const shouldShowColumn = column =>
		resolvedVisibleColumns.some(
			visibleColumn => visibleColumn.value === column
		);

	const handleSelectAll = e => {
		if (e.target.checked) {
			setSelectedIds(data.map(row => row.id));
		} else {
			setSelectedIds([]);
		}
	};

	const handleSelectRow = (e, rowId) => {
		e.stopPropagation();
		if (e.target.checked) {
			setSelectedIds(prev => [...prev, rowId]);
		} else {
			setSelectedIds(prev => prev.filter(id => id !== rowId));
		}
	};

	const isAllSelected = data.length > 0 && selectedIds.length === data.length;
	const isPartiallySelected =
		selectedIds.length > 0 && selectedIds.length < data.length;

	const canUseBulkActions = hasPermission(PERMISSIONS.APPLICANT_BULK_ACTIONS);

	const handleBulkChangeStage = async stage => {
		const affectedIds = [...selectedIds];
		try {
			const result = await applicantStore.bulkUpdate(affectedIds, {
				stage: stage.value
			});
			if (result.status === 'success') {
				notificationStore.pushSuccessNotification(
					'Stage Updated',
					`Successfully updated ${result.data.updated} candidate(s) to ${stage.label}`
				);
				affectedIds.forEach(id => {
					eventManager.emit('applicantStageChanged', { applicantId: id });
				});
				onSuccess?.();

				offerJobApplicationCascade({
					newStage: stage.value,
					applicantIds: affectedIds,
					onSuccess
				});
			} else {
				notificationStore.pushErrorNotification(
					'Update Failed',
					'Failed to update candidates'
				);
			}
		} catch (error) {
			notificationStore.pushErrorNotification(
				'Update Failed',
				'Failed to update some candidates'
			);
		}
	};

	const handleRowChangeStage = async (row, stage) => {
		await applicantStore.update(row.id, {
			applicant: { stage: stage.value }
		});
		onSuccess?.();

		eventManager.emit('applicantStageChanged', {
			applicantId: row.id
		});

		offerJobApplicationCascade({
			newStage: stage.value,
			applicantIds: row.id,
			onSuccess
		});
	};

	const handleBulkAssignToJob = () => {
		modalStore.openModal(
			{
				title: 'Assign to an Open Job',
				position: 'center',
				size: 'md'
			},
			<AssignToJobModal
				applicantIds={selectedIds}
				onSuccess={() => {
					onSuccess?.();
				}}
			/>
		);
	};

	const handleBulkDelete = () => {
		dialogStore.openDialog(
			{
				title: 'Delete Candidates',
				message: `Are you sure you want to delete ${selectedIds.length} candidate(s)? This action cannot be undone.`,
				confirmLabel: 'Delete',
				confirmButtonClass: 'is-danger'
			},
			async () => {
				try {
					const result = await applicantStore.bulkDelete(selectedIds);
					if (result.status === 'success') {
						notificationStore.pushSuccessNotification(
							'Candidates Deleted',
							`Successfully deleted ${result.data.deleted} candidate(s)`
						);
						setSelectedIds([]);
						onSuccess?.();
					} else {
						notificationStore.pushErrorNotification(
							'Delete Failed',
							'Failed to delete candidates'
						);
					}
				} catch (error) {
					notificationStore.pushErrorNotification(
						'Delete Failed',
						'Failed to delete some candidates'
					);
				}
			}
		);
	};

	const getBulkActions = () => {
		if (!canUseBulkActions) {
			return [];
		}

		const stageItems = applicantStore.stageOptions.map(stage => {
			const statusData = getApplicantStatus(
				applicantStore.stageToStatus[stage.value]
			);
			return {
				label: (
					<div className="is-flex is-align-items-center gap-2">
						<ApplicantStageIndicator
							stageLabel={stage?.label}
							statusLabel={statusData?.label}
							stageLevel={stage?.level}
							statusColor={statusData?.color}
						/>
						<span>{stage.label}</span>
					</div>
				),
				onClick: () => handleBulkChangeStage(stage)
			};
		});

		return [
			{
				label: 'Change Internal Stage',
				items: stageItems
			},
			{
				label: 'Assign to a Job',
				onClick: handleBulkAssignToJob
			},
			{
				label: 'Delete',
				isDanger: true,
				onClick: handleBulkDelete
			}
		];
	};

	const getMenuItems = row => {
		if (!hasPermission(PERMISSIONS.APPLICANT_ROW_ACTIONS)) {
			return [];
		}

		return [
			{
				label: 'Change Internal Stage',
				subItems: applicantStore.stageOptions
					.filter(stage => stage.value !== row?.stage)
					.map(stage => {
						const statusData = getApplicantStatus(
							applicantStore.stageToStatus[stage.value]
						);

						return {
							...stage,
							label: (
								<div className="is-flex is-align-items-center gap-2">
									<ApplicantStageIndicator
										stageLabel={stage?.label}
										statusLabel={statusData?.label}
										stageLevel={stage?.level}
										statusColor={statusData?.color}
									/>
									<span>{stage.label}</span>
								</div>
							),
							onClick: () => handleRowChangeStage(row, stage)
						};
					})
			}
		];
	};

	const isProfileOpen =
		drawerStore.isOpen &&
		[
			drawerStore.DRAWER_IDS.JOB_POSITION_PROFILE,
			drawerStore.DRAWER_IDS.APPLICANT_PROFILE,
			drawerStore.DRAWER_IDS.CLIENT_PROFILE
		].includes(drawerStore.drawerId);

	const isApplicantProfileOpen =
		drawerStore.isOpen &&
		drawerStore.drawerId === drawerStore.DRAWER_IDS.APPLICANT_PROFILE;

	const renderTableHeader = () => (
		<thead>
			<tr>
				{canUseBulkActions && (
					<th className="checkbox-column">
						<div className="checkbox-cell">
							<input
								type="checkbox"
								checked={isAllSelected}
								ref={input => {
									if (input) {
										input.indeterminate = isPartiallySelected;
									}
								}}
								onChange={handleSelectAll}
							/>
						</div>
					</th>
				)}
				<th style={{ width: '32px' }}></th>
				{shouldShowColumn('name') && (
					<th className="is-sortable" onClick={() => handleHeaderClick('name')}>
						Name {isColumnSorted('name')}
					</th>
				)}
				{shouldShowColumn('linkedin') && !isProfileOpen && (
					<th
						className="is-sortable has-text-centered"
						onClick={() => handleHeaderClick('linkedinUrl')}>
						Linkedin {isColumnSorted('linkedinUrl')}
					</th>
				)}
				{shouldShowColumn('internal_stage') && (
					<th
						className="is-sortable internal-stage-column-header"
						onClick={() => handleHeaderClick('stage')}>
						Internal Stage {isColumnSorted('stage')}
					</th>
				)}
				{isProfileOpen ? null : (
					<>
						{shouldShowColumn('latest_task') && (
							<th className="latest-task-column-header">Latest Task</th>
						)}
						{shouldShowColumn('last_engagement_date') && (
							<th
								className="is-sortable"
								onClick={() => handleHeaderClick('$lastEngagementAt')}>
								Last Engagement Date {isColumnSorted('$lastEngagementAt')}
							</th>
						)}
						{shouldShowColumn('source') && (
							<th
								className="is-sortable has-text-centered"
								onClick={() => handleHeaderClick('source.name')}>
								Source {isColumnSorted('source.name')}
							</th>
						)}
						{shouldShowColumn('hireability') && (
							<th
								className="is-sortable has-text-centered"
								onClick={() => handleHeaderClick('hireability')}>
								Hireability {isColumnSorted('hireability')}
							</th>
						)}
						{shouldShowColumn('current_compensation') && (
							<th
								className="is-sortable"
								onClick={() =>
									handleHeaderClick('candidate.$currentCompensation')
								}>
								Current Compensation{' '}
								{isColumnSorted('candidate.$currentCompensation')}
							</th>
						)}
						{shouldShowColumn('expected_compensation') && (
							<th
								className="is-sortable expected-compensation-column-header has-text-left"
								onClick={() =>
									handleHeaderClick('candidate.$expectedCompensation')
								}>
								<div className="is-flex is-align-items-center expected-compensation-th-inner">
									<EllipsisTooltipLabel
										wrapperClassName="expected-compensation-header-tooltip"
										className="expected-compensation-header-label"
										text="Expected Compensation"
									/>
									{isColumnSorted('candidate.$expectedCompensation')}
								</div>
							</th>
						)}
						{shouldShowColumn('seniority') && (
							<th
								className="is-sortable"
								onClick={() => handleHeaderClick('seniority')}>
								Seniority {isColumnSorted('seniority')}
							</th>
						)}
						{shouldShowColumn('english_level') && (
							<th
								className="is-sortable"
								onClick={() => handleHeaderClick('candidate.englishLevel')}>
								English Level {isColumnSorted('candidate.englishLevel')}
							</th>
						)}
						{shouldShowColumn('country') && (
							<th
								className="is-sortable country-column-header has-text-left"
								onClick={() => handleHeaderClick('candidate.country.name')}>
								<div className="is-flex is-align-items-center country-th-inner">
									<EllipsisTooltipLabel
										wrapperClassName="country-header-tooltip"
										className="country-header-label"
										text="Country"
										tooltipPlacement="top"
									/>
									{isColumnSorted('candidate.country.name')}
								</div>
							</th>
						)}
						{shouldShowColumn('owner') && (
							<th
								className="is-sortable has-text-centered"
								onClick={() => handleHeaderClick('owner.name')}>
								Owner {isColumnSorted('owner.name')}
							</th>
						)}
						{shouldShowColumn('created_at') && (
							<th
								className="is-sortable"
								onClick={() => handleHeaderClick('createdAt')}>
								Created At {isColumnSorted('createdAt')}
							</th>
						)}
						{shouldShowColumn('updated_at') && (
							<th
								className="is-sortable"
								onClick={() => handleHeaderClick('updatedAt')}>
								Updated At {isColumnSorted('updatedAt')}
							</th>
						)}
						{shouldShowColumn('availability') && (
							<th
								className="is-sortable"
								onClick={() => handleHeaderClick('candidate.lookingForJob')}>
								Availability {isColumnSorted('candidate.lookingForJob')}
							</th>
						)}
						{shouldShowColumn('email') && (
							<th
								className="is-sortable"
								onClick={() => handleHeaderClick('email')}>
								Email {isColumnSorted('email')}
							</th>
						)}
						{shouldShowColumn('phone_number') && (
							<th
								className="is-sortable"
								onClick={() => handleHeaderClick('phoneNumber')}>
								Phone {isColumnSorted('phoneNumber')}
							</th>
						)}
						{shouldShowColumn('city') && (
							<th
								className="is-sortable"
								onClick={() => handleHeaderClick('city')}>
								City {isColumnSorted('city')}
							</th>
						)}
						{shouldShowColumn('github_url') && (
							<th
								className="is-sortable"
								onClick={() => handleHeaderClick('githubUrl')}>
								Github {isColumnSorted('githubUrl')}
							</th>
						)}
						{shouldShowColumn('portfolio_url') && (
							<th
								className="is-sortable"
								onClick={() => handleHeaderClick('portfolioUrl')}>
								Website {isColumnSorted('portfolioUrl')}
							</th>
						)}
					</>
				)}
				<th className="has-text-centered action-btn">
					{isProfileOpen ? null : (
						<ColumnSelector
							target={props => (
								<button type="button" className="action-btn" {...props}>
									<img src="/ats/icons/plus-icon.svg" alt="Add button" />
								</button>
							)}
							value={resolvedVisibleColumns}
							options={applicantStore.tableColumnsOptions}
							onChange={values => onVisibleColumnsChange?.(values)}
						/>
					)}
				</th>
				<th className="pending-task-column"></th>
			</tr>
		</thead>
	);

	const renderTableRow = row => {
		const stageData = getApplicantStage(row.stage);
		const statusData = getApplicantStatus(row.status);
		const isSelected = selectedIds.includes(row.id);
		const expectedCompensationDisplay = row.expectedCompensation
			? `${row.expectedCurrency} ${row.expectedCompensation}/${row.expectedScheme === 'hourly' ? 'Hr' : 'Mo'}`
			: '-';

		return (
			<tr
				key={row.id}
				className={`${isApplicantProfileOpen && String(activeRowId) === String(row.id) ? 'is-active' : ''} ${isSelected ? 'is-selected' : ''} ${menuOpenRowId === row.id ? 'is-menu-open' : ''}`}
				onClick={event => {
					event.stopPropagation();

					if (!['BUTTON', 'INPUT'].includes(event.target.tagName)) {
						onClickRow?.(row);
					}
				}}>
				{canUseBulkActions && (
					<td className="checkbox-column">
						<div className="checkbox-cell">
							<input
								type="checkbox"
								checked={isSelected}
								onChange={e => handleSelectRow(e, row.id)}
							/>
						</div>
					</td>
				)}
				<td className="profile-avatar-column">
					<div className="profile-avatar-cell">
						<UserProfileAvatar
							imageUrl={row.imageUrl || row.candidate?.imageUrl}
							name={row.name}
							size={28}
							showTooltip={false}
						/>
					</div>
				</td>
				{shouldShowColumn('name') && (
					<td className="name-column">
						<div>{row.name ? row.name : ''}</div>
					</td>
				)}
				{shouldShowColumn('linkedin') && !isProfileOpen && (
					<td className="linkedin-column">
						{row.linkedinUrl ? (
							<ActionMenu
								buttonIcon={
									<img src="/ats/icons/linkedin-icon.svg" alt="LinkedIn" />
								}
								tooltipText={row.linkedinUrl}
								items={[
									{
										label: 'Copy LinkedIn URL',
										img: '/ats/icons/copy-icon.svg',
										onClick: () => copyTextToClipboard(row.linkedinUrl),
										successLabel: 'LinkedIn URL Copied',
										successImg: '/ats/icons/check-icon.svg',
										resetDelay: 2000
									},
									{
										label: 'Open LinkedIn URL',
										img: '/ats/icons/send-icon.svg',
										onClick: () => window.open(row.linkedinUrl, '_blank')
									}
								]}
							/>
						) : (
							<div className="inactive-icon">
								<img src="/ats/icons/linkedin-icon.svg" alt="LinkedIn" />
							</div>
						)}
					</td>
				)}
				{shouldShowColumn('internal_stage') && (
					<td className="internal-stage-column">
						<InternalStageCell
							stageLabel={stageData?.label}
							statusLabel={statusData?.label}
							stageLevel={stageData?.level}
							statusColor={statusData?.color}
						/>
					</td>
				)}
				{isProfileOpen ? null : (
					<>
						{shouldShowColumn('latest_task') && (
							<td className="latest-task-column">
								<LatestTaskCell latestTask={row.latestTask} />
							</td>
						)}
						{shouldShowColumn('last_engagement_date') && (
							<td>
								{row.lastEngagementAt
									? moment(row.lastEngagementAt).format('lll')
									: '-'}
							</td>
						)}
						{shouldShowColumn('source') && (
							<td className="owner-column">
								{row.source ? (
									<UserProfileAvatar
										imageUrl={row.source?.imageUrl}
										name={row.source?.name}
										size={16}
									/>
								) : (
									'-'
								)}
							</td>
						)}
						{shouldShowColumn('hireability') && (
							<td className="is-capitalized hireability-column">
								<AtsTooltip
									text={getHireabilityTooltipLabel(row.hireability)}
									placement="bottom"
									className="is-flex is-align-items-center is-justify-content-center">
									<img
										src={
											row.hireability
												? `/ats/icons/face-${
														row.hireability === 'yes'
															? 'happy'
															: row.hireability === 'no'
																? 'sad'
																: 'neutral'
													}-icon.svg`
												: '/ats/icons/hireability-empty-icon.svg'
										}
										alt={getHireabilityTooltipLabel(row.hireability)}
									/>
								</AtsTooltip>
							</td>
						)}
						{shouldShowColumn('current_compensation') && (
							<td className="is-capitalized">
								{row.currentCompensation
									? `${row.currentCurrency} ${row.currentCompensation}/${row.currentScheme === 'hourly' ? 'Hr' : 'Mo'}`
									: '-'}
							</td>
						)}
						{shouldShowColumn('expected_compensation') && (
							<td className="is-capitalized expected-compensation-column-cell">
								<EllipsisTooltipLabel
									wrapperClassName="expected-compensation-cell-tooltip"
									className="expected-compensation-cell-label"
									text={expectedCompensationDisplay}
									tooltipPlacement="top"
								/>
							</td>
						)}
						{shouldShowColumn('seniority') && (
							<td className="is-capitalized">
								{candidateSeniorityOptionLabel[row.seniority]}
							</td>
						)}
						{shouldShowColumn('english_level') && (
							<td className="is-capitalized">
								{
									getEnglishLevel(
										row.englishLevel || row.candidate?.englishLevel
									)?.text
								}
							</td>
						)}
						{shouldShowColumn('country') && (
							<td className="is-capitalized country-column-cell">
								<EllipsisTooltipLabel
									wrapperClassName="country-cell-tooltip"
									className="country-cell-label"
									text={row.country?.name || row.candidate?.country?.name || ''}
									tooltipPlacement="top"
								/>
							</td>
						)}
						{shouldShowColumn('owner') && (
							<td className="owner-column">
								{row.owner && (
									<UserProfileAvatar
										imageUrl={row.owner?.imageUrl}
										name={row.owner?.name}
										size={16}
									/>
								)}
							</td>
						)}
						{shouldShowColumn('created_at') && (
							<td>{moment(row.createdAt).format('lll')}</td>
						)}
						{shouldShowColumn('updated_at') && (
							<td>{moment(row.updatedAt).format('lll')}</td>
						)}
						{shouldShowColumn('availability') && (
							<td className="is-capitalized">{row.candidate?.lookingForJob}</td>
						)}
						{shouldShowColumn('email') && <td>{row.email}</td>}
						{shouldShowColumn('phone_number') && <td>{row.phoneNumber}</td>}
						{shouldShowColumn('city') && (
							<td className="is-capitalized">{row.city}</td>
						)}
						{shouldShowColumn('github_url') && <td>{row.githubUrl}</td>}
						{shouldShowColumn('portfolio_url') && <td>{row.portfolioUrl}</td>}
					</>
				)}
				<td className="has-text-centered action-btn">
					{hasPermission(PERMISSIONS.APPLICANT_ROW_ACTIONS) && (
						<HoverMenu
							id={row.id}
							position="left"
							buttonIcon={
								<img src="/ats/icons/three-dots-icon.svg" alt="Menu button" />
							}
							items={getMenuItems(row)}
							onOpenChange={open => setMenuOpenRowId(open ? row.id : null)}
						/>
					)}
				</td>
				<td className="pending-task-column">
					<PendingTaskBadge count={row.pendingTasksCount} variant="count" />
				</td>
			</tr>
		);
	};

	return (
		<>
			{canUseBulkActions && (
				<BulkActionsBar
					selectedCount={selectedIds.length}
					actions={getBulkActions()}
					onClearSelection={() => setSelectedIds([])}
				/>
			)}
			<div className={`ats-table-scroll${isEmpty ? ' is-empty-results' : ''}`}>
				<table className="table is-fullwidth">
					{renderTableHeader()}
					{loading && data.length === 0 ? (
						<AtsTableSkeleton
							columns={Math.max(visibleColumns?.length || 0, 4)}
						/>
					) : (
						!isEmpty && <tbody>{data.map(renderTableRow)}</tbody>
					)}
				</table>
				{isEmpty && <div className="applicants-empty">{emptyState}</div>}
			</div>
		</>
	);
}

export default observer(ApplicantsTable);
